import type {JSONNode} from '@packages/shared/types/JSONNode';
import {
  type Type,
  matchType,
  Types,
  Plugins,
  Component,
} from '@packages/systems/wfdl';
import {
  type Qualified,
  namesEqual,
  qualify,
  typeName,
  getPluginName,
} from '@packages/systems/wfdl-Qualified';
import {once} from '@packages/utilities/memo';
import {emptyArray, constant} from '@packages/utilities/fp/utils';

export const getElementTypeAndDataType = (
  plugins: Plugins.PluginsType,
  dataTypes: Plugins.DataTypes,
  type: string,
  inst: string | undefined
): [Qualified<Component.ComponentName>, undefined | Type] => {
  if (!inst) {
    const elementType = qualify(
      Plugins.name(Plugins.getNameFromElementType(plugins, type)),
      Component.name(type)
    );
    return [elementType, dataTypes.get(elementType)];
  }

  // Prefer the site component in cases where we have plugin components with
  // the same name (such as from libraries) since we don’t have a reliable way
  // to disambiguate today (March 2026).
  const siteComponentElementType = qualify(
    Plugins.name('__SitePlugin'),
    Component.name(inst)
  );
  const siteComponentDataType = dataTypes.get(siteComponentElementType);
  if (siteComponentDataType)
    return [siteComponentElementType, siteComponentDataType];

  const elType = qualify(
    Plugins.name(Plugins.getNameFromElementType(plugins, inst)),
    Component.name(inst)
  );
  return [elType, dataTypes.get(elType)];
};

const _richTextChildrenType = once(() =>
  qualify(Plugins.name('Basic'), typeName('RichTextChildren'))
);

const _slotChildrenType = once(() =>
  qualify(Plugins.name('Slots'), typeName('SlotContent'))
);

const _collectElementsWithLeafType = matchType<(arg1?: any) => Array<string>>({
  Element: constant((id) => {
    return [id];
  }),
  TypeConstructor: (name) => (ids: Array<string>) => {
    if (
      namesEqual(name, _richTextChildrenType()) ||
      namesEqual(name, _slotChildrenType())
    ) {
      return ids;
    }
    return emptyArray;
  },
  _: constant(constant(emptyArray)),
});

/**
 * This function walks a node using it's data type to gather nested
 * JSONNode IDs.
 *
 * ```jsx
 * // Type
 * TRecord({
 *   a: TRecord({
 *      hi: TListOfElement
 *   }),
 *   b: TListOfElement
 * })
 * // Node
 * {
 *   _id: '1',
 *   data: {
 *     a: {
 *       hi: ['2']
 *     },
 *     b: ['3', '4']
 *   }
 * }
 *
 * // Returns ['2', '3', '4']
 * ```
 */
export const collectElementsWithType = (
  data: any,
  type: Type
): Array<string> => {
  /**
   * If we've hit this base case that means the node's data
   * property does not contain the child, instead the `bind`
   * field contains a reference to a parent symbol's overrides
   * which does contain it. In this case, it is the "child" of
   * the parent symbol.
   */
  if (!data) return [];

  const fields = Types.getRecordFields(type);
  if (fields) {
    const keys = Object.keys(fields);
    return keys.flatMap((key) => {
      const fieldType = fields[key];
      // @ts-expect-error - TS2345 - Argument of type 'Type | undefined' is not assignable to parameter of type 'Type'.
      return collectElementsWithType(data[key], fieldType);
    });
  }

  const listItemType = Types.getListItemType(type);

  if (listItemType) {
    if (Array.isArray(data)) {
      return data.flatMap((item) =>
        collectElementsWithType(item, listItemType)
      );
    }
  }

  return _collectElementsWithLeafType(type)(data);
};

export const rootIdsWith =
  (collectChildrenIds: (node: JSONNode) => undefined | Array<string>) =>
  (nodes: Array<JSONNode>): Array<string> => {
    const parentIndex: Record<string, any> = {};

    nodes.forEach((node) => {
      const childrenIds = collectChildrenIds(node);
      if (childrenIds) {
        childrenIds.forEach((k) => {
          parentIndex[k] = node._id;
        });
      }
    });

    return nodes.flatMap((node) =>
      !parentIndex[node._id] ? [node._id] : emptyArray
    );
  };

export const rootIds = (
  plugins: Plugins.PluginsType,
  dataTypes: Plugins.DataTypes
) =>
  rootIdsWith((node) => {
    if (node.type) {
      const [elementType, dataType] = getElementTypeAndDataType(
        plugins,
        dataTypes,
        node.type,
        node.data?.sym?.inst
      );

      if (dataType && getPluginName(elementType) === '__SitePlugin') {
        return collectElementsWithType(node.data?.sym?.overrides, dataType);
      }
    }
    return node.children;
  });
